import React from 'react';
import { Box, Card, Typography, Button, Avatar, Chip, Stack, IconButton } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toggleFavorite } from '../../../actions/users';
import { urlImg, isEmpty, textoDuracion } from '../../common/helpers';
import { fotoDe } from '../../common/imparte';
import { useIrAEntrar } from '../../../utils/destinoAlEntrar';
import { dinero } from '../../../utils/dinero';

// `user` es el proveedor (quien creó el servicio: con él se abre la ficha). `imparte`, si llega, es la persona que lo imparte
// en un grupo o negocio: es a quien muestra la tarjeta.
const ProfileServiceCard = ({ service, user: srvUser, profile, imparte }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.user);
  const allCategories = useSelector(state => state.cat.categories);
  const isFavorite = user?.favorites?.some(f => f.toString() === service._id?.toString());
  // Sin sesión el corazón manda a entrar (antes la petición fallaba sin decir nada); al entrar regresa a este perfil
  const irAEntrar = useIrAEntrar();

  const {
    _id,
    nombre,
    costoServicio,
    tipoCosto,
    img,
    categoria,
    categoriaNombre,
    modalidades,
    ubicacion,
    duracionEstimada,
    unidadDuracion
  } = service;
  const duracion = textoDuracion(duracionEstimada, unidadDuracion);

  const categoryName = React.useMemo(() => {
    if (!categoria) return 'sin categoría';
    // nombre resuelto por el backend (funciona aunque la categoría esté deshabilitada)
    if (categoriaNombre) return categoriaNombre;
    const found = allCategories.find(c => c._id === categoria || c.name === categoria);
    // nunca mostrar el _id crudo
    return found ? found.name : 'sin categoría';
  }, [categoria, categoriaNombre, allCategories]);

  const serviceImg = !isEmpty(img) && img.length > 0
    ? `${urlImg}/servicios/${_id}/${img[0].nombre}`
    : '/search.png';

  const mostrado = imparte && imparte._id ? imparte : srvUser;
  const userAvatar = fotoDe(mostrado, 'w_48,h_48,c_fill') || '/search.png';

  const handleViewDetail = () => {
    const ownerId = srvUser?._id || service.userId || profile?.user;
    navigate(`/servicio/${ownerId}/${_id}`);
  };

  return (
    <Card
      elevation={0}
      sx={{
        display: 'flex',
        borderRadius: '16px',
        border: 1, borderColor: 'sutil',
        overflow: 'hidden',
        mb: 2,
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }
      }}
    >
      {/* Left Image Section */}
      <Box sx={{ position: 'relative', width: { xs: 150, sm: 220 }, height: 200 }}>
        <Box
          component="img"
          src={serviceImg}
          alt={nombre}
          onClick={handleViewDetail}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            cursor: 'pointer'
          }}
        />

        {/* Category Chip */}
        <Chip
          label={categoryName}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            bgcolor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '8px',
            fontSize: '11px',
            '& .MuiChip-label': { px: 1 }
          }}
        />

        {/* Favorite Icon */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (!user?._id) return irAEntrar();
            dispatch(toggleFavorite(_id));
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: isFavorite ? 'primary.main' : 'white',
            bgcolor: isFavorite ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.2)',
            '&:hover': { bgcolor: isFavorite ? 'white' : 'rgba(0,0,0,0.4)' }
          }}
        >
          {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </IconButton>

        {/* Provider Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'white'
          }}
        >
          <Avatar
            src={userAvatar}
            sx={{ width: 24, height: 24, border: '1px solid rgba(255,255,255,0.5)' }}
          />
          <Typography variant="caption" fontWeight="bold" sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
            {mostrado?.name || profile?.nombre || 'Proveedor'}
          </Typography>
        </Box>
      </Box>

      {/* Right Content Section */}
      <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="h6" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' }, lineHeight: 1.2 }}>
                {nombre}
              </Typography>
              {service.ratingPromedio > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StarIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="caption" fontWeight="bold" color="primary">{service.ratingPromedio.toFixed(1)}</Typography>
                </Box>
              ) : (
                <Typography variant="caption" fontWeight="bold" color="text.secondary">Nuevo</Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {ubicacion?.direccion?.split(',')[0] || modalidades?.[0] || 'Ubicación'}
                </Typography>
              </Box>
              {duracion && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">{duracion}</Typography>
                </Box>
              )}
            </Stack>

            {/* Cuántas opiniones tiene de verdad (como la ficha del servicio); sin opiniones no se dice nada */}
            <Typography variant="caption" color="text.secondary">
              {service.ratingTotal > 0 && `${service.ratingTotal} ${service.ratingTotal === 1 ? 'opinión' : 'opiniones'}`}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'right', ml: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 0, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {dinero(costoServicio)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              por {tipoCosto || 'servicio'}
            </Typography>

            <Button
              variant="contained"
              onClick={handleViewDetail}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: '8px',
                px: { xs: 1.5, sm: 3 },
                fontSize: { xs: '12px', sm: '14px' }
              }}
            >
              Ver detalle
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default ProfileServiceCard;
